import type { HTMLProps, MouseEventHandler } from "react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { css, cx, styled } from "simplydo/react/styled";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Label, type LabelProps } from "simplydo/react/ui/label";

const CircularButtonWithNumberWrapper = styled.div<{ $square?: boolean }>`
  display: flex;
  position: relative;
  flex-direction: row;
  align-items: flex-start;
  > .action-button {
    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: ${({ $square }) => ($square ? "10%" : "50%")};
    font-size: 20px;
    background-color: rgb(240, 240, 240);
    transition: background-color 0.25s;
    height: 30px;
    width: 30px;
    cursor: pointer;

    ${Icon} {
      transition: color 0.25s;
      color: rgb(120, 136, 150);
    }

    a {
      transition: color 0.25s;
      color: rgb(120, 136, 150);
    }

    &:hover {
      background-color: ${({ theme }) => theme.primaryColour};
      a,
      ${Icon} {
        color: ${({ theme }) => theme.colors.primary.onSolid};
      }
    }
  }
  img {
    object-fit: cover;
    width: 30px !important;
    height: 30px !important;
    border-radius: ${({ $square }) => ($square ? "10%" : "50%")};
  }
`;

const numberLabelClass = css`
  position: absolute;
  top: -6px;
  right: -8px;
  z-index: 300;
`;

type CircularButtonWithNumberProps = Omit<
  HTMLProps<HTMLDivElement>,
  "onClick" | "onKeyDown"
> & {
  icon?: IconData;
  image?: string;
  labelProps?: LabelProps;
  count?: number | string;
  square?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
};

const CircularButtonWithNumber = ({
  icon,
  image,
  count,
  labelProps = {},
  square,
  onClick,
  style,
  className,
  id,
  title,
}: CircularButtonWithNumberProps) => (
  <CircularButtonWithNumberWrapper
    $square={square}
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={handleClickForKeyboard(onClick ? () => onClick : undefined)}
    style={style}
    className={className}
    id={id}
    title={title}
  >
    <div className="action-button">
      {icon ? <Icon icon={icon} /> : null}
      {image ? <img src={image} alt="org favicon" /> : null}
    </div>
    {count ? (
      <Label
        {...labelProps}
        circular={labelProps.circular ?? true}
        size={labelProps.size ?? "small"}
        color={labelProps.color ?? "red"}
        className={cx(numberLabelClass, labelProps.className)}
      >
        {count}
      </Label>
    ) : null}
  </CircularButtonWithNumberWrapper>
);

export default CircularButtonWithNumber;
